import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import './ToastContext.css';

/**
 * Lightweight, dependency-free toast system.
 *
 * Wrap a subtree in <ToastProvider> and call useToast() inside it:
 *   const toast = useToast();
 *   toast.success('Saved!');
 *   toast.error('Something broke');
 *
 * Toasts stack top-right, auto-dismiss (errors linger longer), can be
 * dismissed manually, and never show more than MAX_VISIBLE at once.
 * Written generically so the provider can move up the tree later.
 */

const ToastContext = createContext(null);

const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 5000;
const ERROR_DURATION = 8000;

const ICONS = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
};

let nextId = 0;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timers = useRef(new Map());

    const dismiss = useCallback((id) => {
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const push = useCallback((type, message) => {
        if (!message) return;
        const id = ++nextId;
        const duration = type === 'error' ? ERROR_DURATION : DEFAULT_DURATION;

        setToasts((prev) => {
            // Keep the newest MAX_VISIBLE-1 so the incoming one fits.
            const trimmed = prev.slice(-(MAX_VISIBLE - 1));
            return [...trimmed, { id, type, message }];
        });

        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
    }, [dismiss]);

    const api = useMemo(() => ({
        success: (message) => push('success', message),
        error: (message) => push('error', message),
        warning: (message) => push('warning', message),
        info: (message) => push('info', message),
    }), [push]);

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div className="toast-container" role="region" aria-label="Notifications" aria-live="polite">
                {toasts.map((toast) => (
                    <div key={toast.id} className={`toast toast-${toast.type}`} role="status">
                        <span className="toast-icon" aria-hidden="true">{ICONS[toast.type]}</span>
                        <span className="toast-message">{toast.message}</span>
                        <button
                            type="button"
                            className="toast-dismiss"
                            aria-label="Dismiss notification"
                            onClick={() => dismiss(toast.id)}
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return ctx;
}

export default ToastContext;
